import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Checkbox, FormControlLabel, Typography } from "@mui/material";
import Page from "./Page";
import EntityList from "./EntityList";
import Dialog_Flex from "../ui/Dialog_Flex";
import Input from "../elements/Input";
import useData from "../../hooks/useData";
import usePage from "../../hooks/usePage";

const OrdersClassificationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { executeAction } = useData();
  const { showMessage } = usePage();

  const clientId = location?.state?.clientId || null;
  const returnPath = location?.state?.returnPath || "/clientorders";
  const returnState = location?.state?.returnState || {};

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    isActive: true,
    isDefault: false,
  });

  useEffect(() => {
    if (!clientId) {
      setRows([]);
      return;
    }
    loadClassifications();
  }, [clientId]);

  const sortedRows = useMemo(() => {
    return [...rows].sort((left, right) => {
      const leftDefault = left?.isDefault ? 1 : 0;
      const rightDefault = right?.isDefault ? 1 : 0;
      if (leftDefault !== rightDefault) return rightDefault - leftDefault;

      return String(left?.name || "").localeCompare(String(right?.name || ""));
    });
  }, [rows]);

  const loadClassifications = async () => {
    if (!clientId) return;

    setLoading(true);
    try {
      const results = await executeAction({
        entityName: "ClientLocationClassification",
        action: "get",
        filter: { clientId },
      });

      if (results?.isSuccessful && Array.isArray(results.data)) {
        setRows(results.data);
      } else {
        setRows([]);
        showMessage(
          results?.message || "Failed to load classifications",
          "error",
        );
      }
    } catch (err) {
      setRows([]);
      showMessage(err?.message || "Failed to load classifications", "error");
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = () => {
    setEditingRecord(null);
    setFormData({
      code: "",
      name: "",
      isActive: true,
      isDefault: false,
    });
    setOpenDialog(true);
  };

  const openEditDialog = (record) => {
    setEditingRecord(record);
    setFormData({
      code: record?.code || "",
      name: record?.name || "",
      isActive: record?.isActive !== false,
      isDefault: record?.isDefault === true,
    });
    setOpenDialog(true);
  };

  const updateFormField = (field, value) => {
    setFormData((previous) => ({ ...previous, [field]: value }));
  };

  const normalizeCode = (value) => {
    return String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  };

  const unsetOtherDefaults = async (targetId = null) => {
    const currentDefaults = rows.filter(
      (row) => row?.isDefault && (!targetId || row.id !== targetId),
    );

    for (const row of currentDefaults) {
      await executeAction({
        entityName: "ClientLocationClassification",
        action: "update",
        data: {
          id: row.id,
          isDefault: false,
        },
      });
    }
  };

  const saveClassification = async () => {
    if (!clientId) {
      showMessage("Missing client context", "error");
      return;
    }

    const code = normalizeCode(formData.code);
    const name = String(formData.name || "").trim();
    if (!code) {
      showMessage("Classification code is required", "error");
      return;
    }

    if (!name) {
      showMessage("Classification name is required", "error");
      return;
    }

    setSaving(true);
    try {
      if (formData.isDefault) {
        await unsetOtherDefaults(editingRecord?.id || null);
      }

      const data = {
        clientId,
        code,
        name,
        sortOrder: 0,
        isActive: !!formData.isActive,
        isDefault: !!formData.isDefault,
      };

      const results = await executeAction({
        entityName: "ClientLocationClassification",
        action: editingRecord?.id ? "update" : "add",
        data: editingRecord?.id ? { ...data, id: editingRecord.id } : data,
      });

      if (!results?.isSuccessful) {
        showMessage(
          results?.message || "Failed to save classification",
          "error",
        );
        return;
      }

      showMessage("Classification saved", "success");
      setOpenDialog(false);
      await loadClassifications();
    } catch (err) {
      showMessage(err?.message || "Failed to save classification", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleToolbarClick = (name) => {
    if (name === "back") {
      navigate(returnPath, { state: returnState });
      return;
    }

    if (name === "add") {
      openAddDialog();
      return;
    }

    if (name === "refresh") {
      loadClassifications();
    }
  };

  const handleRowClick = (_column, id) => {
    const selected = rows.find((row) => row.id === id);
    if (!selected) return;
    openEditDialog(selected);
  };

  const tableColumns = [
    { name: "code", label: "Code", clickable: true, width: "180px" },
    { name: "name", label: "Name", clickable: true, width: "260px" },
    {
      name: "isDefault",
      label: "Default",
      width: "120px",
      render: (row) => (row?.isDefault ? "Yes" : "No"),
    },
    {
      name: "isActive",
      label: "Active",
      width: "120px",
      render: (row) => (row?.isActive ? "Yes" : "No"),
    },
  ];

  return (
    <>
      <Page
        pageName="Order Classifications"
        includeToolbar
        onToolItemClick={handleToolbarClick}
        items={[
          { name: "back", label: "Back", iconName: "ArrowBack" },
          { name: "add", label: "Add", iconName: "AddCircleOutline" },
          { name: "refresh", label: "Refresh", iconName: "Refresh" },
        ]}
      >
        {!clientId ? (
          <Typography>Client context is missing for this page.</Typography>
        ) : (
          <Box>
            <Typography sx={{ mb: 1 }}>
              Manage client location classifications used by Orders policy
              resolution.
            </Typography>

            {loading ? (
              <Typography>Loading classifications...</Typography>
            ) : (
              <EntityList
                entityList={sortedRows}
                columns={tableColumns}
                onRowClick={handleRowClick}
                tableHeight="calc(100vh - 250px)"
                allowSort={false}
              />
            )}
          </Box>
        )}
      </Page>

      <Dialog_Flex
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        title={editingRecord?.id ? "Edit Classification" : "Add Classification"}
        message=""
        actions={[
          { name: "cancel", label: "cancel" },
          { name: "save", label: saving ? "saving" : "save" },
        ]}
        onAction={(action) => {
          if (action === "cancel") {
            setOpenDialog(false);
            return;
          }
          if (action === "save" && !saving) {
            saveClassification();
          }
        }}
      >
        <Box
          sx={{
            px: 3,
            pb: 2,
            pt: 1,
            minWidth: 420,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Input
            label="Code"
            value={formData.code}
            onChange={(value) => updateFormField("code", value)}
          />

          <Input
            label="Name"
            value={formData.name}
            onChange={(value) => updateFormField("name", value)}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={!!formData.isActive}
                onChange={(_event, checked) =>
                  updateFormField("isActive", checked)
                }
              />
            }
            label="Active"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={!!formData.isDefault}
                onChange={(_event, checked) =>
                  updateFormField("isDefault", checked)
                }
              />
            }
            label="Default classification"
          />
        </Box>
      </Dialog_Flex>
    </>
  );
};

export default OrdersClassificationPage;
